import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertTriangle, FileText } from "lucide-react";

/**
 * Polls the payment provider status endpoint after checkout success redirect.
 * Supports Stripe (session_id) and Paystack (paystack_reference / reference / trxref).
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const paystackRef = params.get("paystack_reference") || params.get("reference") || params.get("trxref");
  const provider = sessionId ? "stripe" : (paystackRef ? "paystack" : null);
  const [state, setState] = useState({ status: "polling", paymentStatus: "pending", ref: null, error: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!provider) { setState({ status: "error", error: "Référence de paiement introuvable" }); return; }
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const url = provider === "paystack"
          ? `/payments/paystack/verify/${paystackRef}`
          : `/payments/stripe/status/${sessionId}`;
        const { data } = await api.get(url);
        if (data.payment_status === "paid" || data.payment_status === "deposit_paid") {
          setState({ status: "success", paymentStatus: data.payment_status, ref: data.booking_ref });
          return;
        }
        if (data.payment_status === "payment_received" || data.payment_status === "payment_received_full") {
          setState({ status: "awaiting_validation", paymentStatus: data.payment_status, ref: data.booking_ref });
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState({ status: "error", paymentStatus: data.payment_status, ref: data.booking_ref, error: "Paiement non finalisé" });
          return;
        }
        if (attemptsRef.current > 20) {
          setState({ status: "timeout", paymentStatus: data.payment_status, ref: data.booking_ref });
          return;
        }
        timerRef.current = setTimeout(poll, 1500);
      } catch (e) {
        if (attemptsRef.current > 5) {
          setState({ status: "error", error: extractError(e) });
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [provider, sessionId, paystackRef]);

  const goToMyBookings = () => navigate("/my-bookings");

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="payment-success-page">
      <SiteHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="bg-white rounded-3xl p-8 md:p-12 border border-gray-100 mbk-card-shadow text-center">
          {state.status === "polling" && (
            <>
              <Loader2 className="h-14 w-14 mx-auto text-[#0e7a3f] animate-spin" />
              <h1 className="font-display text-2xl font-black text-[#0a1e3f] mt-6">Vérification de votre paiement…</h1>
              <p className="text-sm text-gray-500 mt-2">Nous confirmons votre paiement en toute sécurité. Ne fermez pas cette page.</p>
            </>
          )}
          {state.status === "success" && (
            <>
              <div className="h-16 w-16 rounded-full bg-[#dcfce7] text-[#0e7a3f] flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8" /></div>
              <h1 className="font-display text-3xl font-black text-[#0a1e3f] mt-6" data-testid="payment-success-title">Paiement confirmé !</h1>
              <p className="text-sm text-gray-600 mt-2">
                Votre réservation <b data-testid="payment-success-ref">{state.ref}</b> a été mise à jour comme <b>payée</b>.
                Un email de confirmation avec votre contrat PDF vient de vous être envoyé.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button data-testid="go-my-bookings" onClick={goToMyBookings} className="mbk-btn-primary text-white h-11 px-6 rounded-full gap-2">
                  <FileText className="h-4 w-4" /> Voir mes réservations
                </Button>
                <Link to="/" className="text-sm font-semibold text-[#0e7a3f] hover:underline">Retour à l'accueil</Link>
              </div>
            </>
          )}
          {state.status === "awaiting_validation" && (
            <>
              <div className="h-16 w-16 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8" /></div>
              <h1 className="font-display text-3xl font-black text-[#0a1e3f] mt-6" data-testid="payment-awaiting-title">Paiement bien reçu !</h1>
              <p className="text-sm text-gray-600 mt-2">
                Votre réservation <b data-testid="payment-awaiting-ref">{state.ref}</b> est en attente de <b>vérification par notre équipe MOBIKAR</b>.
              </p>
              <p className="text-sm text-gray-500 mt-3">
                Dès validation des fonds sur notre compte, votre <b>contrat de location</b> vous sera envoyé automatiquement
                par email. Cela prend généralement quelques heures ouvrées.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button data-testid="go-my-bookings-awaiting" onClick={goToMyBookings} className="mbk-btn-primary text-white h-11 px-6 rounded-full gap-2">
                  <FileText className="h-4 w-4" /> Voir mes réservations
                </Button>
                <Link to="/" className="text-sm font-semibold text-[#0e7a3f] hover:underline">Retour à l'accueil</Link>
              </div>
            </>
          )}
          {state.status === "timeout" && (
            <>
              <Loader2 className="h-14 w-14 mx-auto text-amber-500" />
              <h1 className="font-display text-2xl font-black text-[#0a1e3f] mt-6">Paiement en cours de traitement</h1>
              <p className="text-sm text-gray-500 mt-2">
                La confirmation prend plus de temps que prévu. Votre réservation sera automatiquement mise à jour
                dès réception de la confirmation.
              </p>
              <div className="mt-6">
                <Button onClick={goToMyBookings} data-testid="go-my-bookings-timeout" className="mbk-btn-primary text-white h-11 px-6 rounded-full">Voir mes réservations</Button>
              </div>
            </>
          )}
          {state.status === "error" && (
            <>
              <AlertTriangle className="h-14 w-14 mx-auto text-red-500" />
              <h1 className="font-display text-2xl font-black text-[#0a1e3f] mt-6">Un problème est survenu</h1>
              <p className="text-sm text-gray-500 mt-2">{state.error || "Paiement non confirmé. Aucun montant ne vous sera prélevé."}</p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button onClick={goToMyBookings} className="mbk-btn-primary text-white h-11 px-6 rounded-full">Mes réservations</Button>
                <Link to="/contact" className="text-sm font-semibold text-[#0e7a3f] hover:underline">Contacter le support</Link>
              </div>
            </>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
